import { useContext } from 'react';
import FeatureFlagsContext from '@/context/FeatureFlagsContext';

/**
 * Returned when no FeatureFlagsProvider is mounted above the consumer. The
 * flags read as "not resolved yet" (undefined) rather than false, so callers
 * that route on a flag value don't act on a fabricated answer.
 */
const NO_PROVIDER = {
  isFetching: true,
  client: null,
  getStreamlinedAccountFlowEnabled: () => undefined,
  getFellowshipCTA: () => undefined,
  getFeedbackCTA: () => undefined,
  getLowerNetworkBarriersStewards: () => undefined
};

/**
 * Reads the feature flags from the single, shared Statsig client owned by
 * FeatureFlagsProvider (mounted above Master, see routes.js).
 *
 * The provider is initialized with the current user, so the `currentUser`
 * argument is accepted only for backwards compatibility with existing call
 * sites and is ignored.
 *
 * @param {import('../models/currentUser').LoreModel} [_currentUser] - Ignored.
 * @returns {{
 *   isFetching: boolean,
 *   client: Object,
 *   getStreamlinedAccountFlowEnabled: function(): (boolean|undefined),
 *   getFellowshipCTA: function(): (boolean|undefined),
 *   getFeedbackCTA: function(): (boolean|undefined),
 *   getLowerNetworkBarriersStewards: function(): (boolean|undefined)
 * }}
 */
export default function useFeatureFlags(_currentUser) {
  const featureFlags = useContext(FeatureFlagsContext);

  if (!featureFlags) {
    console.error('useFeatureFlags: no FeatureFlagsProvider found above this component');
    return NO_PROVIDER;
  }

  return featureFlags;
}
